import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, deleteDoc, doc, getDoc, setDoc, updateDoc, writeBatch, increment } from 'firebase/firestore';
import { db } from './firebase.utils';
import { sanitizeForFirestore } from './serialization';
import { stripCardForLot } from '../whatnot/whatnotListingText';
import { EBAY_LOTS_CATEGORY_ID, EBAY_SINGLES_CATEGORY_ID, buildEbayLotDescription } from '../ebay/ebayListingText';
import { sumItemPrices } from '../ebay/ebayLotPrice';
import { buildEbaySku } from '../ebay/ebaySku';
import { reserveGroupCode } from './groupCodes';
import { isStaged, SALE_KIND, SALE_STATUSES } from '../sales/sales.constants';

const EBAY_PLATFORM = 'eBay';

const ebayQueueFields = () => ({
    kind: SALE_KIND,
    platform: EBAY_PLATFORM,
    status: SALE_STATUSES.STAGED,
});

const MAX_CODE_ATTEMPTS = 8;

/**
 * Mints one SKU for `sequence` and reserves it globally. A taken suffix is
 * retried; the sequence number is not advanced again.
 *
 * @param {string} uid
 * @param {number} sequence
 * @returns {Promise<string>}
 */
async function mintOneEbaySku(uid, sequence, source = 'ebay') {
    for (let attempt = 0; attempt < MAX_CODE_ATTEMPTS; attempt++) {
        const sku = buildEbaySku(sequence);
        const reserved = await reserveGroupCode(uid, sku, { source });
        if (reserved) return reserved;
    }
    throw new Error('Could not reserve a unique listing code');
}

/**
 * Reserves `count` SKUs against the user's listing counter and the global
 * group-code map.
 *
 * The counter is advanced with a non-transactional increment because a race
 * can only affect ordering; uniqueness is the `groupCodes` write.
 *
 * @param {string} uid - User ID
 * @param {number} count - How many SKUs to reserve
 * @param {string} [source='ebay']
 * @returns {Promise<string[]>} Newly minted SKUs
 */
export async function mintEbaySkus(uid, count = 1, source = 'ebay') {
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    const start = (snap.exists() ? snap.data().ebaySkuSeq : 0) || 0;

    await setDoc(userRef, { ebaySkuSeq: increment(count) }, { merge: true });

    const skus = [];
    for (let i = 0; i < count; i++) {
        skus.push(await mintOneEbaySku(uid, start + 1 + i, source));
    }
    return skus;
}

/**
 * Backfills SKUs onto queued items that predate SKU minting, so items already
 * staged still export with a matchable custom label.
 *
 * @param {string} uid - User ID
 * @param {Array<Object>} items - Queue items
 * @returns {Promise<Object>} Map of item id to newly assigned SKU
 */
export async function ensureEbaySkus(uid, items) {
    const missing = (items ?? []).filter((item) => !item.sku);
    if (!uid || missing.length === 0) return {};

    const skus = await mintEbaySkus(uid, missing.length);
    const batch = writeBatch(db);
    const assigned = {};

    missing.forEach((item, index) => {
        assigned[item.id] = skus[index];
        batch.update(doc(db, 'users', uid, 'transactions', item.id), { sku: skus[index] });
    });

    await batch.commit();
    return assigned;
}

/**
 * Adds a single card variation to the user's eBay staging collection.
 * prefs = { categoryId, conditionId, defaultFormat, defaultPrice }
 */
export async function addCardToEbay(uid, cardData, variation, context, prefs = {}) {
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = context;
    const [sku] = await mintEbaySkus(uid);

    const docData = sanitizeForFirestore({
        sku,
        // Card identity
        cardNumber:       cardData.cardNumber ?? null,
        name:             cardData.name ?? [],
        team:             cardData.team ?? [],
        cardAttributes:   cardData.cardAttributes ?? [],
        // Variation
        cardParallelName: variation.cardParallelName ?? null,
        cardColor:        variation.cardColor ?? null,
        cardPrintNumber:  variation.cardPrintNumber ?? null,
        cardSupply:       variation.cardSupply ?? null,
        // Checklist context
        sport:            sport ?? null,
        year:             year ?? null,
        manufacturer:     manufacturer ?? null,
        series:           series ?? null,
        cardType:         cardType ?? null,
        cardTypeCategory: cardTypeCategory ?? null,
        // eBay listing fields
        categoryId:       prefs.categoryId    ?? '261328',
        conditionId:      prefs.conditionId   ?? '2750',
        format:           prefs.defaultFormat ?? 'FixedPrice',
        price:            prefs.defaultPrice  ?? '1',
        // Staging metadata
        type:    'single',
        ...ebayQueueFields(),
        addedAt: serverTimestamp(),
    });

    const colRef = collection(db, 'users', uid, 'transactions');
    const docRef = await addDoc(colRef, docData);
    return docRef.id;
}

/**
 * Adds a custom (on-the-fly) single to the eBay queue.
 */
export async function addCustomCardToEbay(uid, cardFields, prefs = {}) {
    const [sku] = await mintEbaySkus(uid);

    const docData = sanitizeForFirestore({
        sku,
        cardNumber:       cardFields.cardNumber       ?? null,
        name:             cardFields.name             ?? [],
        team:             cardFields.team             ?? [],
        cardAttributes:   cardFields.cardAttributes   ?? [],
        cardParallelName: cardFields.cardParallelName ?? null,
        cardColor:        cardFields.cardColor        ?? null,
        cardPrintNumber:  cardFields.cardPrintNumber  ?? null,
        cardSupply:       cardFields.cardSupply       ?? null,
        sport:            null,
        year:             null,
        manufacturer:     null,
        series:           cardFields.series           ?? null,
        cardType:         null,
        cardTypeCategory: null,
        // eBay listing fields
        categoryId:       prefs.categoryId    ?? '261328',
        conditionId:      prefs.conditionId   ?? '2750',
        format:           prefs.defaultFormat ?? 'FixedPrice',
        price:            prefs.defaultPrice  ?? '1',
        type:    'single',
        ...ebayQueueFields(),
        addedAt: serverTimestamp(),
    });

    const colRef = collection(db, 'users', uid, 'transactions');
    const docRef = await addDoc(colRef, docData);
    return docRef.id;
}

/**
 * Subscribes to real-time updates on the user's eBay staging collection.
 */
export function listenToEbayCards(uid, callback) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const q = query(colRef, orderBy('addedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const items = snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((item) => item.platform === EBAY_PLATFORM && isStaged(item.status));
        callback(items);
    }, () => callback([]));
}

/**
 * Removes a single item from the user's eBay staging collection.
 */
export async function removeCardFromEbay(uid, docId) {
    const docRef = doc(db, 'users', uid, 'transactions', docId);
    await deleteDoc(docRef);
}

/**
 * Updates specific fields on an eBay item (e.g. price, format overrides).
 */
export async function updateEbayItem(uid, docId, data) {
    const docRef = doc(db, 'users', uid, 'transactions', docId);
    await updateDoc(docRef, sanitizeForFirestore(data));
}

/**
 * Combines staged singles into one lot document and removes the source singles.
 * prefs = { conditionId, format }
 * The lot price is the sum of each card's queue price. Lots always use the
 * Trading Card Lots category (261329).
 */
export async function createEbayLot(uid, title, cardIds, cards, prefs = {}) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const lotRef = doc(colRef);
    const embeddedCards = cards.map(stripCardForLot);
    // A lot is one eBay listing, so it carries its own SKU; the SKUs of the
    // singles it replaces retire with their documents.
    const [sku] = await mintEbaySkus(uid);

    const batch = writeBatch(db);
    batch.set(lotRef, sanitizeForFirestore({
        sku,
        type:        'lot',
        title:       title.trim(),
        description: buildEbayLotDescription(cards),
        cards:       embeddedCards,
        cardCount:   cards.length,
        categoryId:  EBAY_LOTS_CATEGORY_ID,
        conditionId: prefs.conditionId ?? '2750',
        format:      prefs.format      ?? 'FixedPrice',
        price:       sumItemPrices(cards),
        ...ebayQueueFields(),
        addedAt:     serverTimestamp(),
    }));

    cardIds.forEach((id) => {
        batch.delete(doc(db, 'users', uid, 'transactions', id));
    });

    await batch.commit();
    return lotRef.id;
}

/**
 * Restores a lot's embedded cards as singles and deletes the lot document.
 * Restored singles use Trading Card Singles (not the lot category).
 */
export async function ungroupEbayLot(uid, lotItem) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const restoredCards = lotItem.cards ?? [];
    // Each restored single becomes its own listing, so each needs its own SKU.
    const skus = await mintEbaySkus(uid, Math.max(1, restoredCards.length));
    const batch = writeBatch(db);

    restoredCards.forEach((card, index) => {
        const singleRef = doc(colRef);
        batch.set(singleRef, sanitizeForFirestore({
            sku:         skus[index],
            ...stripCardForLot(card),
            categoryId:  EBAY_SINGLES_CATEGORY_ID,
            conditionId: lotItem.conditionId ?? '2750',
            format:      lotItem.format      ?? 'FixedPrice',
            price:       lotItem.price       ?? '1',
            type:        'single',
            ...ebayQueueFields(),
            addedAt:     serverTimestamp(),
        }));
    });

    batch.delete(doc(db, 'users', uid, 'transactions', lotItem.id));
    await batch.commit();
}
